import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ResponseArtifactDTO,
  UpdateResponseArtifactResponse,
  UpdateResponseArtifactRequest,
} from '@dreamai/types';
import api from '../../api';
import ResponseArtifactModal from './sidebar/ResponseArtifactModal';
import { isOrganisationChargeError } from './sidebar/organisationSpend';
import { isHotelQuoteExpiredError } from './sidebar/hotelArtifact';
import InlineMultipleChoice from './InlineMultipleChoice';
import type { CardSelection } from './sidebar/SpendingApprovalModal';
import { ReactComponent as HourglassIcon } from '../../icons/hourglass.svg';
import { ReactComponent as CheckSmallIcon } from '../../icons/check-small.svg';
import { ReactComponent as DocumentTextIcon } from '../../icons/document-text.svg';
import './task-escalation-card.scss';
import './inline-response-artifact.scss';

/**
 * Inline card rendered in ThreadView when the brancher calls
 * `showResponseArtifact`. Styled to match `TaskStartedPane` —
 * shows an icon frame, title, and the appropriate CTA:
 *   - `multiple_choice` renders `InlineMultipleChoice` directly
 *   - All other interactive types open `ResponseArtifactModal`
 *   - `information_dump` with projectAppId navigates to research
 */
import { isAwaitingReturnTimes } from './responseArtifactState';

interface InlineResponseArtifactProps {
  artifactId: string;
  /** Parent thread ID — used for research document navigation. */
  threadId: string;
  /**
   * True once the secretary has said or shown anything after this card. A
   * flights card whose "See return times" press is still being worked on
   * stays in its "finding" state until then.
   */
  /**
   * Tells whether the secretary did anything after the given moment (ms).
   * Used to end the "Finding return times…" state once a follow-up exists.
   */
  hasLaterActivity?: (sinceMs: number | null) => boolean;
}

const MODAL_FRIENDLY_TYPES = new Set([
  'multiple_choice',
  'yes_or_no',
  'form_input',
  'spending_approval',
  'confirmation_query',
  'expense_review',
]);

const getOpenLabel = (artifact: ResponseArtifactDTO): string => {
  const type = artifact.artifactType as string;
  if (type === 'form_input')
    return artifact.confirmationId ? 'Open approval' : 'Open form';
  if (type === 'spending_approval') return 'Open approval';
  if (type === 'multiple_choice') return 'Open options';
  if (type === 'yes_or_no') return 'Open question';
  if (type === 'confirmation_query') return 'Open question';
  if (type === 'expense_review') return 'Review expenses';
  if (type === 'information_dump') return 'Open research';
  return 'Review and respond';
};

const InlineResponseArtifact: React.FC<InlineResponseArtifactProps> = ({
  artifactId,
  threadId,
  hasLaterActivity,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [, setSearchParams] = useSearchParams();
  const [artifact, setArtifact] = useState<ResponseArtifactDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/response-artifacts/artifact/${artifactId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data.success) setArtifact(res.data.artifact);
      } catch (err) {
        console.error('InlineResponseArtifact: fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [artifactId, getAccessTokenSilently]);

  const handleOptimisticAction = useCallback(
    (_art: ResponseArtifactDTO, action: 'submit' | 'dismiss') => {
      if (action !== 'submit') return;
      // Stamp the press locally too: the server sets actionedAt, but until the
      // card is re-fetched this timestamp is what tells "Finding return
      // times…" which secretary activity came after the press.
      setArtifact((prev) =>
        prev
          ? {
              ...prev,
              status: 'actioned',
              actionedAt: prev.actionedAt ?? new Date().toISOString(),
            }
          : prev,
      );
    },
    [],
  );

  const handleSubmitResponse = useCallback(
    async (
      art: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
        }>;
      },
      cardSelection?: CardSelection,
    ) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: art.id,
          status: 'actioned',
          userResponse: response,
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${art.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setArtifact((prev) =>
          prev
            ? {
                ...prev,
                status: 'actioned',
                userResponse: response,
                actionedAt: prev.actionedAt ?? new Date().toISOString(),
              }
            : prev,
        );
        setModalOpen(false);
      } catch (err) {
        if (isOrganisationChargeError(err) || isHotelQuoteExpiredError(err))
          throw err;
        console.error('InlineResponseArtifact: submit failed', err);
      }
    },
    [getAccessTokenSilently],
  );

  if (loading) {
    return (
      <div className='inline-response-artifact'>
        <div
          className='task-status-pane status-started'
          style={{ minHeight: 168 }}
        />
      </div>
    );
  }

  if (!artifact) return null;

  const type = artifact.artifactType as string;
  const isActioned = artifact.status === 'actioned';
  const pressedAt = artifact.actionedAt
    ? Date.parse(artifact.actionedAt)
    : null;
  const laterActivity = hasLaterActivity
    ? hasLaterActivity(Number.isNaN(pressedAt) ? null : pressedAt)
    : false;
  const awaitingReturns = isAwaitingReturnTimes(artifact, laterActivity);
  // An answered card stays read-only; while return times are still being
  // found only the header wording changes, so the card cannot be pressed twice.
  const isDone = isActioned;

  // Multiple choice renders inline — cream wrapper with header. The header
  // also opens the full view: interactive while pending (fare/rate selection
  // and Book), read-only once actioned so the chosen option and any booking
  // result stay reachable from the chat.
  if (type === 'multiple_choice') {
    return (
      <div className='inline-response-artifact inline-response-artifact--mc'>
        <div className='ira-mc-header'>
          <span className='ira-mc-header-label'>
            {awaitingReturns
              ? 'Finding return times…'
              : isDone
                ? 'Task complete'
                : 'Waiting for your response'}
          </span>
          <button
            type='button'
            className='ira-mc-header-open'
            onClick={() => setModalOpen(true)}
          >
            {isDone ? 'View' : getOpenLabel(artifact)}
          </button>
        </div>
        <InlineMultipleChoice artifactId={artifactId} threadId={threadId} />
        <ResponseArtifactModal
          show={modalOpen}
          onClose={() => setModalOpen(false)}
          artifacts={[artifact]}
          initialIndex={0}
          onSubmitResponse={handleSubmitResponse}
          onOptimisticAction={handleOptimisticAction}
          readOnly={isDone}
        />
      </div>
    );
  }

  // Resolve projectAppId for information_dump research navigation
  const projectAppId =
    artifact.projectAppId ||
    artifact.linkedArtifacts?.find((la) => la.projectAppId)?.projectAppId ||
    undefined;

  // Information dump renders as a compact pill link
  if (type === 'information_dump' && projectAppId) {
    return (
      <div className='inline-response-artifact'>
        <button
          type='button'
          className='ira-research-pill'
          onClick={() =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set('app_tab', 'documents');
                next.delete('app_id');
                next.set('research_app_id', projectAppId);
                return next;
              },
              { replace: true },
            )
          }
        >
          <DocumentTextIcon className='ira-research-pill-icon' />
          <span>{artifact.title || 'Research document'}</span>
        </button>
      </div>
    );
  }

  const title = artifact.title || 'Action required';
  const subtitle = isDone ? 'Completed' : 'Waiting for your response';

  const isModalFriendly = MODAL_FRIENDLY_TYPES.has(type);

  return (
    <div className='inline-response-artifact'>
      <div className='task-status-pane status-started'>
        <span className='status-icon-frame'>
          {isDone ? (
            <CheckSmallIcon className='status-step-icon status-complete' />
          ) : (
            <HourglassIcon className='status-step-icon status-waiting' />
          )}
        </span>

        <h3 className='status-title'>{title}</h3>
        <p className='status-subtitle'>{subtitle}</p>

        {/* Modal-friendly CTA */}
        {isModalFriendly && type !== 'multiple_choice' && (
          <div className='status-artifact-opener'>
            <button
              type='button'
              className='confirm-btn'
              disabled={isDone}
              onClick={() => setModalOpen(true)}
            >
              {isDone ? '✓ Done' : getOpenLabel(artifact)}
            </button>
          </div>
        )}

        {/* Non-modal, non-research: full-screen navigation */}
        {!isModalFriendly && !projectAppId && !isDone && (
          <div className='status-artifact-opener'>
            <button
              type='button'
              className='confirm-btn'
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('app_tab', 'documents');
                    next.delete('app_id');
                    return next;
                  },
                  { replace: true },
                )
              }
            >
              {getOpenLabel(artifact)}
            </button>
          </div>
        )}
      </div>

      {/* Modal for interactive artifact types */}
      {isModalFriendly && (
        <ResponseArtifactModal
          show={modalOpen}
          onClose={() => setModalOpen(false)}
          artifacts={[artifact]}
          initialIndex={0}
          onSubmitResponse={handleSubmitResponse}
          onOptimisticAction={handleOptimisticAction}
          readOnly={isDone}
        />
      )}
    </div>
  );
};

export default InlineResponseArtifact;
